import React, { useCallback, useEffect, useState } from 'react';
import { Select, Table, Alert, App } from 'antd';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import productService from '../../services/productService';
import pricingGroupService from '../../services/pricingGroupService';
import productPricingTiersService from '../../services/productPricingTiersService';

const PARTNER_GROUP_NAME = 'PARTNER';

export default function SharePriceChartPage() {
  const { message } = App.useApp();

  const [productOptions, setProductOptions] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productId, setProductId] = useState(undefined);

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [pricingGroupId, setPricingGroupId] = useState(undefined);

  const [tiers, setTiers] = useState([]);
  const [tiersError, setTiersError] = useState(null);
  const [tiersLoading, setTiersLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await pricingGroupService.list();
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        setGroups(arr);
        const partner = arr.find((g) => g.group_name === PARTNER_GROUP_NAME);
        if (partner) setPricingGroupId(partner.id);
      } catch (e) {
        if (!cancelled) message.error(e?.message || 'Failed to load pricing groups');
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [message]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setProductSearchLoading(true);
      try {
        const res = await productService.getProducts({ search: productSearch, limit: 50 });
        if (cancelled) return;
        const list = res?.data?.products;
        setProductOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setProductOptions([]);
      } finally {
        if (!cancelled) setProductSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [productSearch]);

  const loadTiers = useCallback(async () => {
    if (!productId || !pricingGroupId) {
      setTiers([]);
      setTiersError(null);
      return;
    }
    setTiersLoading(true);
    setTiersError(null);
    try {
      const { tiers: list, tiersQtyValid, tiersQtyError } = await productPricingTiersService.list(
        productId,
        pricingGroupId,
      );
      setTiers(Array.isArray(list) ? list : []);
      setTiersError(tiersQtyValid === false ? tiersQtyError : null);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load price chart');
      setTiers([]);
    } finally {
      setTiersLoading(false);
    }
  }, [productId, pricingGroupId, message]);

  useEffect(() => {
    loadTiers();
  }, [loadTiers]);

  const formatQty = (v) => String(Math.round(Number(v)));

  const columns = [
    { title: 'Order', dataIndex: 'tier_order', key: 'tier_order', width: 80 },
    { title: 'Min qty', dataIndex: 'min_qty', key: 'min_qty', render: (v) => formatQty(v) },
    {
      title: 'Max qty',
      dataIndex: 'max_qty',
      key: 'max_qty',
      render: (v) => (v == null || v === '' ? '— (open)' : formatQty(v)),
    },
    { title: 'Price / unit', dataIndex: 'pricing_per_unit', key: 'pricing_per_unit' },
    { title: 'Min price', dataIndex: 'min_price', key: 'min_price' },
    {
      title: 'Max price',
      dataIndex: 'max_price',
      key: 'max_price',
      render: (v) => (v == null || v === '' ? '— (open)' : v),
    },
  ];

  const productSelectOptions = productOptions.map((p) => ({
    value: p.id,
    label: p.name || p.slug || p.id,
  }));

  const groupSelectOptions = groups.map((g) => ({
    value: g.id,
    label: g.group_name || g.id,
  }));

  const bothSelected = Boolean(productId && pricingGroupId);

  return (
    <DashboardPageWrapper>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Share Price Chart</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          View per-unit pricing tiers for a product against a pricing group. Pricing group defaults to{' '}
          <strong>PARTNER</strong>; the chart loads automatically when you change the product or the group.
        </p>
      </div>

      <div className="flex flex-wrap gap-4 items-end mb-4 bg-white p-4 rounded-2xl border border-gray-100">
        <div className="min-w-[240px] flex-1">
          <div className="text-xs text-gray-500 mb-1">Product</div>
          <Select
            showSearch
            allowClear
            placeholder="Search product by name"
            className="w-full"
            value={productId}
            onChange={setProductId}
            onSearch={setProductSearch}
            filterOption={false}
            loading={productSearchLoading}
            notFoundContent={productSearchLoading ? 'Searching…' : 'No products found'}
            options={productSelectOptions}
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <div className="text-xs text-gray-500 mb-1">Pricing group</div>
          <Select
            showSearch
            placeholder="Select pricing group"
            optionFilterProp="label"
            className="w-full"
            loading={groupsLoading}
            value={pricingGroupId}
            onChange={setPricingGroupId}
            options={groupSelectOptions}
            allowClear
          />
        </div>
      </div>

      {tiersError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message="Quantity range validation"
          description={tiersError}
        />
      )}

      <div className="bg-white rounded-2xl border border-gray-100 p-0 overflow-hidden">
        <Table
          rowKey={(r) => r.id}
          columns={columns}
          dataSource={tiers}
          loading={tiersLoading}
          pagination={false}
          locale={{
            emptyText: bothSelected
              ? 'No tiers found for this product and pricing group.'
              : 'Select a product and pricing group to view the price chart.',
          }}
        />
      </div>
    </DashboardPageWrapper>
  );
}
